import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { resetStore } from "app/actions";
import {
  endGame,
  forcedEndGame,
  getCurrentSession,
  getSessions,
  startGame,
  startSession,
} from "app/thunks/sessionsAPI";
import { Match } from "interfaces/Match";
import { UpdateSessionRequest } from "interfaces/requests/Sessions";
import {
  GetSessionResponse,
  StartSessionResponse,
} from "interfaces/responses/Sessions";
import { Session, SESSION_STATUS } from "interfaces/Sessions";

import { RootState } from "../../app/store";

export interface HeadSet {
  connectionId: string;
  createdAt?: string;
  description: string;
  deviceId: string;
  isActive?: boolean;
  status: boolean;
  updatedAt?: string;
  _id: string;
}

export interface State {
  isFetching: boolean;
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: any;
  list: GetSessionResponse[];
  selectedContainers: string[];
  selectedContainersStatus: SESSION_STATUS | undefined;
  sessionStarted: boolean;
  sessionId?: string;
  canStartSession?: boolean;
  isSessionCompleted?: boolean;
  canStartGame: boolean;
  headsetStatus?: string[];
  startDate?: Date;
  endDate?: Date;
  matches?: Match[];
  session?: Session;
}

const initialSelectedContainers: string[] = [];

const initialState: State = {
  isFetching: false,
  isLoading: false,
  isSuccess: false,
  isError: false,
  error: undefined,
  sessionStarted: false,
  list: [],
  selectedContainersStatus: undefined,
  selectedContainers: initialSelectedContainers,
  sessionId: undefined,
  canStartSession: false,
  isSessionCompleted: false,
  canStartGame: false,
  headsetStatus: [],
  startDate: undefined,
  endDate: undefined,
  matches: [],
  session: undefined,
};

export const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    addSelectedContainer(
      state,
      action: PayloadAction<{ configId: string; status?: SESSION_STATUS }>,
    ) {
      state.selectedContainers = [action.payload.configId];
      state.selectedContainersStatus = action.payload.status;
    },
    removeSelectedContainer(state, action: PayloadAction<string>) {
      const newSelectedContainers = state.selectedContainers.filter(
        (container) => container !== action.payload,
      );
      state.selectedContainers = newSelectedContainers;
    },
    resetSelectedContainers(state) {
      state.selectedContainers = initialSelectedContainers;
    },
    resetFlags: (state) => {
      state.isSuccess = false;
      state.isLoading = false;
      state.isError = false;
      state.isSessionCompleted = false;
    },
    resetSession: (state) => {
      state.selectedContainers = initialSelectedContainers;
      state.sessionId = "";
      state.sessionStarted = false;
      state.startDate = undefined;
      state.matches = [];
    },
    updateSession: (state, action: PayloadAction<UpdateSessionRequest>) => {
      state.canStartSession = action.payload?.canStartGame;
      state.startDate = action.payload?.startDate;
      state.endDate = action.payload?.endDate;
      state.isSessionCompleted = action.payload?.isSessionCompleted;
    },
    saveHeadsetStatus: (state, action) => {
      state.headsetStatus = action.payload;
    },
    saveMatches: (state, action) => {
      state.matches = action.payload;
    },
    setSession(state, action: PayloadAction<Session>) {
      state.session = action.payload;
    },
    unsetSession(state) {
      state.session = undefined;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(resetStore, () => initialState)
      .addCase(startSession.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(startSession.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(
        startSession.fulfilled,
        (state, action: PayloadAction<StartSessionResponse>) => {
          state.isSuccess = true;
          state.isLoading = false;
          state.sessionStarted = true;
          state.sessionId = action.payload._id;
        },
      )
      .addCase(startGame.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(startGame.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(startGame.fulfilled, (state) => {
        state.isSuccess = true;
        state.isLoading = false;
        state.sessionStarted = true;
      })
      .addCase(endGame.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(endGame.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(endGame.fulfilled, (state, action) => {
        state.isSuccess = true;
        state.isLoading = false;
        state.sessionStarted = false;
        state.isSessionCompleted = action.payload.isSessionCompleted;
      })
      .addCase(forcedEndGame.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(forcedEndGame.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(forcedEndGame.fulfilled, (state) => {
        state.isSuccess = true;
        state.isLoading = false;
        state.sessionStarted = false;
      })
      .addCase(getCurrentSession.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getCurrentSession.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(
        getCurrentSession.fulfilled,
        (state, action: PayloadAction<GetSessionResponse>) => {
          state.isSuccess = true;
          state.isLoading = false;
          state.sessionId = action.payload._id;
        },
      )
      .addCase(getSessions.pending, (state) => {
        state.isFetching = true;
      })
      .addCase(getSessions.rejected, (state, action) => {
        state.isFetching = false;
        state.isError = true;
        state.error = action.payload;
      })
      .addCase(
        getSessions.fulfilled,
        (state, action: PayloadAction<GetSessionResponse[]>) => {
          state.isFetching = false;
          state.list = action.payload;
        },
      );
  },
});

export const {
  addSelectedContainer,
  removeSelectedContainer,
  resetSelectedContainers,
  resetFlags,
  resetSession,
  updateSession,
  saveHeadsetStatus,
  saveMatches,
  setSession,
  unsetSession,
} = sessionSlice.actions;

// ------ Selectors ------

export const getSessionActionState = (state: RootState) => ({
  isFetching: state.session.isFetching,
  isLoading: state.session.isLoading,
  isSuccess: state.session.isSuccess,
  isError: state.session.isError,
  error: state.session.error,
  list: state.session.list,
  sessionId: state.session.sessionId,
  selectedContainers: state.session.selectedContainers,
  canStartSession: state.session.canStartSession,
  isSessionCompleted: state.session.isSessionCompleted,
  headsetStatus: state.session.headsetStatus,
  startDate: state.session.startDate,
  endDate: state.session.endDate,
  sessionStarted: state.session.sessionStarted,
  session: state.session.session,
});

export const getSelectedContainers = (state: RootState) => ({
  selectedContainers: state.session.selectedContainers,
  selectedContainersStatus: state.session.selectedContainersStatus,
});

export const checkSessionState = (state: RootState) => ({
  isSessionStarted: state.session.sessionStarted,
});

export const getHeadsetStatus = (state: RootState) => ({
  headsetStatus: state.session.headsetStatus,
});

export const getMatches = (state: RootState) => ({
  matches: state.session.matches,
});

export default sessionSlice.reducer;
